import { useCallback, useEffect, useMemo } from 'react'

import { useAuth0 } from '@auth0/auth0-react'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { atom, useAtom } from 'jotai'
import { jwtDecode } from 'jwt-decode'

import { Permission } from '@forto-dev/auth-sdk-models'

import { userQueryOptions } from '@/features/users'
import { useAnalytics } from '@/utils/analytics'
import { getUserDetailsFromCurrentToken, isTokenValid } from '@/utils/auth'
import { addRumScope, grantRumTrackingConsent, removeRumScope, revokeRumTrackingConsent } from '@/utils/monitoring'
import { removeItem, removeSessionItem, setItem, setSessionItem } from '@/utils/storage'
import { userlane } from '@/utils/userlane/UserlaneService'

import { isLoggingOutAtom, tokenAtom } from './auth.store'
import { LoginPath, resolveLoginPath } from './loginV1Flag.service'
import { useCustomerAccountData } from './useCustomerAccountData'
import { useLogin } from './useLogin'
import { useLogout } from './useLogout'
import { setLastCustomerId } from '../domain/auth0'
import { mapAccountResponseToCustomer } from '../domain/customer'
import { getPermissionsFromToken } from '../domain/permission'
import { hasPermission as permissionsInclude } from '../service/permissions'

const extractPlainTokenFromAuthenticationResponse = (token: string): string => token.replace(/^JWT /, '').trim()

type LoginProps = {
  email: string
  password: string
}

type JwtTokenData = {
  customerId: string
  userId: string
  loggedInAsCustomer?: boolean
  tokenRequestedFrom?: string
}

const isAuthenticatedAtom = atom<boolean>(false)
const loggedInAsCustomerAtom = atom<boolean>(false)
const tokenRequestedFromAtom = atom<string | undefined>('')

export const useAuth = () => {
  const [token, setToken] = useAtom(tokenAtom)
  const [isAuthenticated, setIsAuthenticated] = useAtom(isAuthenticatedAtom)
  const [loggedInAsCustomer, setLoggedInAsCustomer] = useAtom(loggedInAsCustomerAtom)
  const [tokenRequestedFrom, setTokenRequestedFrom] = useAtom(tokenRequestedFromAtom)
  const [isLoggingOut, setIsLoggingOut] = useAtom(isLoggingOutAtom)
  const loginMutation = useLogin()
  const logoutMutation = useLogout()
  const queryClient = useQueryClient()
  const customerAccountData = useCustomerAccountData()
  const analyticsService = useAnalytics()
  const { isAuthenticated: isAuth0Authenticated, logout: auth0Logout } = useAuth0()

  const unAuthenticate = useCallback(() => {
    setLoggedInAsCustomer(false)
    setIsAuthenticated(false)
    setToken(undefined)
    removeItem('token')
    removeSessionItem('token')
    removeRumScope()
    revokeRumTrackingConsent()
    analyticsService.resetAnalytics()
    userlane.hide()
  }, [analyticsService, setIsAuthenticated, setLoggedInAsCustomer, setToken])

  const authenticate = useCallback(
    (token: string) => {
      const tokenData = jwtDecode<JwtTokenData>(token)
      const isImpersonating = tokenData.loggedInAsCustomer ?? false

      setIsAuthenticated(true)
      setToken(token)
      setItem('token', token)
      if (isImpersonating) {
        setSessionItem('token', token)
      } else {
        // A previous Login As token would otherwise shadow this one, as getCurrentToken reads sessionStorage first
        removeSessionItem('token')
      }
      setLoggedInAsCustomer(isImpersonating)
      setTokenRequestedFrom(tokenData.tokenRequestedFrom)
      // Kept for the unauthenticated /login route and for logout, which have the login v1 flag
      // evaluated for this customer to pick the login page
      if (tokenData.customerId) {
        setLastCustomerId(tokenData.customerId)
      }
    },
    [setIsAuthenticated, setLoggedInAsCustomer, setToken, setTokenRequestedFrom]
  )

  const fetchCustomerAccountData = useCallback(async () => {
    await customerAccountData.refetch()
  }, [customerAccountData])

  const loginWithToken = useCallback(
    async (token: string) => {
      authenticate(token)
      await fetchCustomerAccountData()
    },
    [authenticate, fetchCustomerAccountData]
  )

  const login = useCallback(
    async (body: LoginProps) => {
      await loginMutation.mutateAsync(
        {
          email: body.email,
          password: body.password,
        },
        {
          onSuccess: async (data) => {
            await loginWithToken(extractPlainTokenFromAuthenticationResponse(data.token))
          },
        }
      )
    },
    [loginMutation, loginWithToken]
  )

  /**
   * Returns the login page the caller must navigate to, or undefined when Auth0 has taken over the
   * page with its own logout redirect
   */
  const logout = useCallback(async (): Promise<LoginPath | undefined> => {
    unAuthenticate()
    await logoutMutation.mutateAsync(undefined)

    const loginPath = await resolveLoginPath(queryClient)

    if (!isAuth0Authenticated) return loginPath

    // Auth0 owns the page from here, so stop rendering the router until the browser leaves
    setIsLoggingOut(true)
    await auth0Logout({ logoutParams: { returnTo: `${window.location.origin}${loginPath}` } })

    return undefined
  }, [auth0Logout, isAuth0Authenticated, logoutMutation, queryClient, setIsLoggingOut, unAuthenticate])

  const userIdFromToken = getUserDetailsFromCurrentToken()?.userId
  const userQuery = useQuery({
    ...userQueryOptions(userIdFromToken ?? ''),
    enabled: !!userIdFromToken && isAuthenticated,
  })
  const user = userQuery.data
  const customer = customerAccountData.data ? mapAccountResponseToCustomer(customerAccountData.data) : undefined
  const permissions = useMemo(() => getPermissionsFromToken(token), [token])
  const hasPermission = useCallback(
    (permission: Permission) => permissionsInclude(permissions, permission),
    [permissions]
  )

  useEffect(() => {
    if (!isAuthenticated || !user || !customer) return
    grantRumTrackingConsent()
    addRumScope({
      id: user.id,
      email: user.email,
      company: customer.name,
      customerId: customer._id,
    })
    analyticsService.initAnalytics(customer, user, loggedInAsCustomer, tokenRequestedFrom)
  }, [isAuthenticated, user, customer, loggedInAsCustomer, tokenRequestedFrom, analyticsService])

  return {
    token,
    login,
    logout,
    authenticate,
    unAuthenticate,
    loginWithToken,
    isReady: !customerAccountData.isPending,
    isLoggingIn: loginMutation.isPending || (isAuthenticated && (!customer || !user)),
    isLoggedIn: isAuthenticated && !!customer && !!user,
    shouldHideLogin: customerAccountData.isPending || (!!token && isTokenValid(token)),
    customer,
    isCustomerActive: !!customer?.activatedAt && !customer?.deactivatedAt,
    user,
    permissions,
    hasPermission,
    isAuthenticated,
    isLoggingOut,
    loggedInAsCustomer,
    tokenRequestedFrom,
    isTokenValid: isTokenValid(token),
  }
}
